import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { hetznerApi, accessRequestsApi, resourcesApi } from '../services/api';
import { Resource } from '../types';
import { Plus, HardDrive, RefreshCw, ExternalLink, Trash2, Shield, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { usePermissions } from '../hooks/usePermissions';
import AccessRequestForm from '../components/AccessRequestForm';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { Link } from 'react-router-dom';
import { Banner, Breadcrumbs, Button, Card, EmptyState, IconButton, Input, Modal, PageHeader, Select, StatusBadge, Textarea, OutcomeBadge, Timestamp } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { BRAND } from '@/brand';
import ProviderMark from '@/components/ProviderMark';
import CardGridSkeleton from '@/components/CardGridSkeleton';

export default function HetznerResources() {
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showAccessRequestForm, setShowAccessRequestForm] = useState(false);
  const [selectedResourceForRequest, setSelectedResourceForRequest] = useState<Resource | null>(null);
  const [showRoleMappings, setShowRoleMappings] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [pendingProject, setPendingProject] = useState<any>(null);
  const [hetznerAccessDescription, setHetznerAccessDescription] = useState<string>('');
  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const { isAdmin } = usePermissions();

  const { data: projectsData, isLoading: projectsLoading, isError: listError, refetch: refetchList } = useQuery('hetzner-projects', () => hetznerApi.listProjects());
  const projects = projectsData?.data?.projects || [];

  const createProjectMutation = useMutation((data: any) => hetznerApi.createProject(data), {
    onSuccess: () => { toast.success('Hetzner project added'); queryClient.invalidateQueries('hetzner-projects'); setShowProjectForm(false); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to add project')); },
  });

  const deleteProjectMutation = useMutation((id: string) => hetznerApi.deleteProject(id), {
    onSuccess: () => { toast.success('Project removed'); queryClient.invalidateQueries('hetzner-projects'); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to remove project')); },
  });

  const { data: accessChecks } = useQuery(
    ['hetzner-project-access', projects.map((p: any) => p.id).join(',')],
    async () => {
      const statusMap: Record<string, { hasAccess: boolean; resourceId?: string }> = {};
      for (const proj of projects) {
        const extId = `hetzner-project-${proj.id}`;
        try {
          const resp = await resourcesApi.listAll(undefined, undefined, 200, 0);
          const match = resp.data?.resources?.find((r: any) =>
            r.external_id === extId || (r.type?.startsWith('hetzner_') && r.metadata?.hetzner_project_internal_id === proj.id)
          );
          if (match) {
            const accessResp = await resourcesApi.getAccessStatus(match.id);
            statusMap[proj.id] = { hasAccess: accessResp.data.has_access === true, resourceId: match.id };
          } else { statusMap[proj.id] = { hasAccess: false }; }
        } catch { statusMap[proj.id] = { hasAccess: false }; }
      }
      return statusMap;
    },
    { enabled: projects.length > 0 && !isAdmin(), refetchInterval: 30000 }
  );

  const { data: roleMappingsData } = useQuery('hetzner-role-mappings', () => hetznerApi.listRoleMappings());
  const roleMappings = roleMappingsData?.data?.mappings || [];

  const handleRequestAccess = (proj: any) => {
    setPendingProject(proj);
    setHetznerAccessDescription('');
    setShowDescriptionModal(true);
  };

  const openAccessRequestForm = async (proj: any) => {
    try {
      const resp = await resourcesApi.listAll(undefined, undefined, 200, 0);
      const match = resp.data?.resources?.find((r: Resource) =>
        r.type?.startsWith('hetzner_') && r.metadata?.hetzner_project_internal_id === proj.id
      );
      if (match) { setSelectedResourceForRequest(match); setShowAccessRequestForm(true); }
      else toast.error('Hetzner resource not found. Admin needs to add resources.');
    } catch (err) { toast.error(errorMessage(err, 'Failed to look up resource.')); }
  };

  const userHasAccess = (proj: any): boolean => {
    if (isAdmin()) return true;
    return accessChecks?.[proj.id]?.hasAccess === true;
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        className="mb-6"
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Cloud Providers', href: '/cloud-providers' }, { label: 'Hetzner Cloud' }]}
            renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
          />
        }
        icon={<HardDrive />}
        title="Hetzner Cloud"
        description={`Access Hetzner Cloud resources through ${BRAND.name}`}
        secondaryActions={isAdmin() ? (
          <Button variant="secondary" aria-pressed={showRoleMappings} onClick={() => setShowRoleMappings(!showRoleMappings)}
            leadingIcon={<Shield className="h-4 w-4" />}
            className={clsx(showRoleMappings && 'ring-2 ring-[var(--ds-color-ring)]')}>
            Hetzner Permissions
          </Button>
        ) : undefined}
        primaryAction={isAdmin() ? (
          <Button onClick={() => setShowProjectForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Project
          </Button>
        ) : undefined}
      />

      {projectsLoading ? (
        <CardGridSkeleton label="Loading Hetzner projects" />
      ) : listError ? (
        <Card>
          <EmptyState
            icon={<HardDrive />}
            title="Could not load Hetzner projects"
            description="Something went wrong while fetching the list. Check your connection and try again."
            action={<Button variant="secondary" onClick={() => refetchList()} leadingIcon={<RefreshCw className="h-4 w-4" />}>Retry</Button>}
          />
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            icon={<HardDrive />}
            title="No Hetzner Projects Configured"
            description={`Add a Hetzner Cloud project to enable resource access through ${BRAND.name}.`}
            action={isAdmin() ? (
              <Button onClick={() => setShowProjectForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>Add Project</Button>
            ) : undefined}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((proj: any) => {
            const hasAccess = userHasAccess(proj);
            return (
              <Card key={proj.id} className="group">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <ProviderMark label="HZ" />
                      <div className="min-w-0">
                        <h3 className="text-base font-medium text-[var(--d2d-text-primary)]">{proj.project_name}</h3>
                        <p className="text-xs text-[var(--d2d-text-muted)]">
                          {proj.last_discovery_at ? <>Last scan: <Timestamp value={proj.last_discovery_at} noSeconds /></> : 'Hetzner Cloud Project'}
                        </p>
                      </div>
                    </div>
                    {isAdmin() && (
                      <IconButton aria-label="Remove project" tooltip="Remove project" size="sm" icon={<Trash2 className="h-4 w-4" />}
                        onClick={async () => {
                          if (await confirm({
                            title: 'Remove this project?',
                            message: `${proj.project_name} will be removed from ${BRAND.name}.`,
                            confirmLabel: 'Remove project',
                            variant: 'danger',
                          })) deleteProjectMutation.mutate(proj.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-[var(--danger-text)]" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-[var(--d2d-bg-secondary)]">
                    {hasAccess ? (
                      <StatusBadge value="approved">Access Granted</StatusBadge>
                    ) : (
                      <OutcomeBadge value="required" tone="new">Access Required</OutcomeBadge>
                    )}
                    <StatusBadge className="ml-auto" value={proj.enabled ? 'active' : 'disabled'}>{proj.enabled ? 'Enabled' : 'Disabled'}</StatusBadge>
                  </div>

                  {hasAccess && (
                    <div className="space-y-2">
                      <Button as="a" href="https://console.hetzner.cloud" target="_blank" rel="noopener noreferrer" className="w-full"
                        leadingIcon={<ExternalLink className="h-4 w-4" />}>
                        Open Hetzner Console
                      </Button>
                      {!isAdmin() && (
                        <Button variant="ghost" size="sm" className="w-full" onClick={() => handleRequestAccess(proj)}
                          leadingIcon={<Plus className="h-3.5 w-3.5" />}>
                          Request More Access
                        </Button>
                      )}
                    </div>
                  )}

                  {!hasAccess && (
                    <Button variant="secondary" className="w-full" onClick={() => handleRequestAccess(proj)}
                      leadingIcon={<Clock className="h-4 w-4" />}>
                      Request Access
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showRoleMappings && isAdmin() && (
        <Card className="mt-8">
          <Card.Header
            title="Available Hetzner Permissions"
            description="Permission levels that users can request access to"
            actions={<Button onClick={() => setShowAddMapping(true)} leadingIcon={<Plus className="h-4 w-4" />}>Add Permission</Button>}
          />
          <div className="divide-y divide-[var(--d2d-border)]">
            {roleMappings.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--d2d-text-muted)]">No permissions configured. Add permission levels that users can request access to.</div>
            ) : (
              roleMappings.map((m: any) => (
                <div key={m.id} className="px-6 py-4 flex items-center justify-between hover:bg-[var(--ds-color-bg-hover)]">
                  <div className="flex items-center gap-3 min-w-0">
                    <Shield aria-hidden className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--d2d-text-primary)]">{m.permission_name}</div>
                      {m.api_token_scope && <div className="text-xs text-[var(--d2d-text-secondary)] mt-0.5">{m.api_token_scope}</div>}
                    </div>
                  </div>
                  <IconButton aria-label="Remove permission" tooltip="Remove permission" size="sm" icon={<Trash2 className="h-4 w-4" />}
                    className="hover:text-[var(--danger-text)]"
                    onClick={async () => {
                      if (await confirm({
                        title: 'Remove this permission?',
                        message: `${m.permission_name} will no longer be available to request.`,
                        confirmLabel: 'Remove permission',
                        variant: 'danger',
                      })) hetznerApi.deleteRoleMapping(m.id)
                        .then(() => { queryClient.invalidateQueries('hetzner-role-mappings'); toast.success('Removed'); })
                        .catch((err) => { toast.error(errorMessage(err, 'Failed to remove permission')); });
                    }} />
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      <Banner variant="info" title="How Hetzner Access Works" className="mt-8">
        {isAdmin()
          ? 'As an admin, you can access the Hetzner Console directly. Use Hetzner Permissions to define access levels (e.g., Read Only, Full Access) that users can request.'
          : 'Click "Request Access" to submit an access request. Once approved by an admin, you can open the Hetzner Console with the assigned permission level.'}
      </Banner>

      {showProjectForm && <AddProjectModal onClose={() => setShowProjectForm(false)} onSubmit={(d) => createProjectMutation.mutate(d)} isLoading={createProjectMutation.isLoading} />}

      {showAddMapping && projects.length > 0 && (
        <AddHetznerPermissionModal projects={projects} onClose={() => setShowAddMapping(false)}
          onSuccess={() => { queryClient.invalidateQueries('hetzner-role-mappings'); setShowAddMapping(false); }} />
      )}

      {showDescriptionModal && pendingProject && (
        <HetznerAccessDescriptionModal projectName={pendingProject.project_name}
          onSubmit={async (description: string) => {
            setShowDescriptionModal(false);
            setHetznerAccessDescription(description);
            await openAccessRequestForm(pendingProject);
            setPendingProject(null);
          }}
          onClose={() => { setShowDescriptionModal(false); setPendingProject(null); }}
        />
      )}

      {showAccessRequestForm && selectedResourceForRequest && (
        <AccessRequestForm resource={selectedResourceForRequest}
          metadata={hetznerAccessDescription ? { hetzner_access_description: hetznerAccessDescription } : undefined}
          onClose={() => { setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setHetznerAccessDescription(''); }}
          onSuccess={() => {
            queryClient.invalidateQueries('hetzner-project-access');
            setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setHetznerAccessDescription('');
            toast.success('Access request submitted!');
          }}
        />
      )}
    </div>
  );
}

function AddProjectModal({ onClose, onSubmit, isLoading }: { onClose: () => void; onSubmit: (d: any) => void; isLoading: boolean }) {
  const formId = useId();
  const [projectName, setProjectName] = useState('');
  const [apiToken, setApiToken] = useState('');
  return (
    <Modal open onClose={onClose} title="Add Hetzner Project" size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading}>{isLoading ? 'Adding...' : 'Add Project'}</Button>
        </>
      }>
      <form id={formId} onSubmit={(e) => { e.preventDefault(); onSubmit({ project_name: projectName, api_token: apiToken }); }} className="space-y-4">
        <Input label="Project Name" placeholder="My Cloud Project" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
        <Input label="API Token" className="[&_input]:font-mono" type="password" placeholder="••••••••" value={apiToken} onChange={(e) => setApiToken(e.target.value)} required
          help="Generate at: Hetzner Cloud Console → Security → API Tokens" />
      </form>
    </Modal>
  );
}

function HetznerAccessDescriptionModal({ projectName, onSubmit, onClose }: { projectName: string; onSubmit: (desc: string) => void; onClose: () => void }) {
  const [description, setDescription] = useState('');
  return (
    <Modal open onClose={onClose} title="Request Hetzner Access" size="md"
      description={<>Describe the access you need for <span className="font-medium">{projectName}</span></>}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { if (description.trim()) onSubmit(description.trim()); }}
            disabled={!description.trim()}
          >Continue</Button>
        </>
      }>
      <div className="space-y-4">
        <Textarea label="What Hetzner access do you need?" rows={4} resize="none"
          placeholder="e.g., I need read-only access to monitor servers, or full access to manage load balancers..."
          value={description} onChange={(e) => setDescription(e.target.value)} autoFocus
          help="The admin will review your request and assign the appropriate permission level." />
        <Banner variant="info">Be specific about the resources you need access to (e.g., "Read-only access to production servers" or "Full access to manage volumes").</Banner>
      </div>
    </Modal>
  );
}

function AddHetznerPermissionModal({ projects, onClose, onSuccess }: { projects: any[]; onClose: () => void; onSuccess: () => void }) {
  const formId = useId();
  const [projId, setProjId] = useState(projects[0]?.id || '');
  const [permName, setPermName] = useState('');
  const [scope, setScope] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await hetznerApi.createRoleMapping({ hetzner_project_id: projId, permission_name: permName, api_token_scope: scope || undefined });
      toast.success('Permission created');
      onSuccess();
    } catch (err) { toast.error(errorMessage(err, 'Failed to create permission')); }
    setIsLoading(false);
  };

  return (
    <Modal open onClose={onClose} title="Add Hetzner Permission" size="md"
      description="Define a permission level that users can request"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading}>{isLoading ? 'Adding...' : 'Add Permission'}</Button>
        </>
      }>
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Select label="Project" value={projId} onChange={(e) => setProjId(e.target.value)}>
          {projects.map((p: any) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
        </Select>
        <Input label="Permission Name" placeholder="e.g., Read Only, Full Access, DNS Manager" value={permName}
          onChange={(e) => setPermName(e.target.value)} required help="A descriptive name for this access level" />
        <Input label="Scope / Description (optional)" placeholder="e.g., Servers read-only, All resources" value={scope} onChange={(e) => setScope(e.target.value)} />
      </form>
    </Modal>
  );
}
